import React, { useState, Suspense, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./EmailTemplates.css";
import {
  getAllEmailTemplatesAPI,
  createEmailTemplateAPI,
  updateEmailTemplateAPI,
  deleteEmailTemplateAPI,
  toggleTemplateStatusAPI,
} from "../../API/EmailTemplateAPI";
import {
  getSchedulesByTemplateAPI,
  createEmailScheduleAPI,
  updateEmailScheduleAPI,
  deleteEmailScheduleAPI,
  pauseEmailScheduleAPI,
  resumeEmailScheduleAPI,
  previewAudienceAPI,
  getUsersForScheduleAPI,
} from "../../API/EmailScheduleAPI";
import { getLogsByTemplateAPI } from "../../API/EmailLogAPI";
import ButtonLoader from "../../components/button/ButtonLoader";
import toast from "react-hot-toast";
import {
  FiMail,
  FiSave,
  FiEdit2,
  FiTrash2,
  FiPlus,
  FiX,
  FiEye,
  FiEyeOff,
  FiAlertCircle,
  FiCode,
  FiClock,
  FiCalendar,
  FiUsers,
  FiPause,
  FiPlay,
  FiSearch,
  FiList,
  FiCheckCircle,
  FiXCircle,
  FiExternalLink,
} from "react-icons/fi";

// Lazy load components
const Wrapper = React.lazy(() => import("../../components/wrapper/Wrapper"));

const templateValidationSchema = Yup.object().shape({
  templateType: Yup.string().required("Template type is required"),
  subject: Yup.string()
    .min(3, "Subject must be at least 3 characters")
    .max(200, "Subject cannot exceed 200 characters")
    .required("Subject is required"),
  htmlContent: Yup.string()
    .min(10, "HTML content must be at least 10 characters")
    .required("HTML content is required"),
  variables: Yup.string(),
  isActive: Yup.boolean(),
});

// Rich Text Editor Configuration
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ font: [] }],
    [{ size: ["small", false, "large", "huge"] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    [{ align: [] }],
    ["blockquote", "code-block"],
    ["link", "image"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "align",
  "blockquote",
  "code-block",
  "link",
  "image",
];

const EmailTemplates = () => {
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [showHtmlEditor, setShowHtmlEditor] = useState(false);

  // Schedule-related state
  const [schedules, setSchedules] = useState([]);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [isEditingSchedule, setIsEditingSchedule] = useState(false);
  const [scheduleLoader, setScheduleLoader] = useState(false);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [audiencePreview, setAudiencePreview] = useState(null);

  // Logs for template
  const [templateLogs, setTemplateLogs] = useState([]);
  const [logsLoader, setLogsLoader] = useState(false);

  const initialValues = {
    templateType: "welcome",
    subject: "",
    htmlContent: "",
    variables: "#name#",
    isActive: true,
  };

  const scheduleInitialValues = {
    name: "",
    scheduleType: "delay",
    delayMinutes: 60,
    scheduledDateTime: "",
    dailyTime: "09:00",
    audienceType: "manual_list",
    singleUser: "",
    manualEmailList: "",
    audienceFilters: {
      incompleteProfile: false,
      joinedBefore: "",
      joinedAfter: "",
      lastLoginBefore: "",
      lastLoginAfter: "",
    },
  };

  const formik = useFormik({
    initialValues,
    validationSchema: templateValidationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const payload = {
          ...values,
          variables: values.variables
            .split(",")
            .map((v) => v.trim())
            .filter((v) => v),
        };

        if (isEditing && selectedTemplate) {
          // Remove templateType from payload when updating (type cannot be changed)
          const { templateType, ...updatePayload } = payload;
          await updateEmailTemplateAPI(selectedTemplate._id, updatePayload);
          toast.success("Email template updated successfully");
        } else {
          await createEmailTemplateAPI(payload);
          toast.success("Email template created successfully");
        }

        fetchTemplates();
        handleCloseModal();
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Error saving email template"
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const [scheduleFormData, setScheduleFormData] = useState(scheduleInitialValues);

  const fetchTemplates = async () => {
    setLoader(true);
    try {
      const res = await getAllEmailTemplatesAPI();
      setTemplates(res.data.templates || []);
    } catch (error) {
      toast.error("Failed to load email templates");
    } finally {
      setLoader(false);
    }
  };

  const fetchSchedulesForTemplate = async (templateId) => {
    setScheduleLoader(true);
    try {
      const res = await getSchedulesByTemplateAPI(templateId);
      setSchedules(res.data.schedules || []);
    } catch (error) {
      console.error("Error fetching schedules:", error);
      setSchedules([]);
    } finally {
      setScheduleLoader(false);
    }
  };

  const fetchTemplateLogs = async (templateId, limit = 5) => {
    setLogsLoader(true);
    try {
      const res = await getLogsByTemplateAPI(templateId, { page: 1, limit });
      setTemplateLogs(res.data.logs || []);
    } catch (error) {
      console.error("Error fetching template logs:", error);
      setTemplateLogs([]);
    } finally {
      setLogsLoader(false);
    }
  };

  const searchUsers = async (search) => {
    setUserSearchLoading(true);
    try {
      const res = await getUsersForScheduleAPI({ search, limit: 20 });
      setUsers(res.data.users || []);
    } catch (error) {
      console.error("Error searching users:", error);
    } finally {
      setUserSearchLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (userSearch) {
        searchUsers(userSearch);
      }
    }, 300);
    return () => clearTimeout(debounce);
  }, [userSearch]);

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setIsEditing(true);
    formik.setValues({
      templateType: template.templateType,
      subject: template.subject,
      htmlContent: template.htmlContent,
      variables: template.variables?.join(", ") || "",
      isActive: template.isActive,
    });
    setIsModalOpen(true);
    
    // Fetch schedules and logs if it's a general template
    if (template.templateType === "general") {
      fetchSchedulesForTemplate(template._id);
      fetchTemplateLogs(template._id);
    }
  };

  const handleCreate = () => {
    setSelectedTemplate(null);
    setIsEditing(false);
    formik.resetForm();
    setSchedules([]);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTemplate(null);
    setIsEditing(false);
    setShowHtmlEditor(false);
    setSchedules([]);
    setTemplateLogs([]);
    formik.resetForm();
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this template?")) {
      try {
        await deleteEmailTemplateAPI(id);
        toast.success("Template deleted successfully");
        fetchTemplates();
      } catch (error) {
        toast.error("Error deleting template");
      }
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await toggleTemplateStatusAPI(id);
      toast.success("Template status updated successfully");
      fetchTemplates();
    } catch (error) {
      toast.error("Error updating template status");
    }
  };

  const handlePreview = (htmlContent) => {
    // Replace variables with sample data for preview
    let preview = htmlContent.replace(/#name#/g, "John Doe");
    preview = preview.replace(/#email#/g, "john@example.com");
    preview = preview.replace(/#otp#/g, "123456");
    preview = preview.replace(/#code#/g, "123456");
    preview = preview.replace(
      /#permissionsList#/g,
      "<li>Manage Users</li><li>Manage Products</li><li>View Dashboard</li>"
    );
    preview = preview.replace(
      /#missingFields#/g,
      '<p style="color: #d9534f;">• Phone Number</p><p style="color: #d9534f;">• Date of Birth</p><p style="color: #d9534f;">• Sports Information</p>'
    );
    preview = preview.replace(/#appLink#/g, "https://app.tykoonsports.com");
    setPreviewHtml(preview);
    setShowPreview(true);
  };

  // Schedule handlers
  const handleOpenScheduleModal = (schedule = null) => {
    if (schedule) {
      setSelectedSchedule(schedule);
      setIsEditingSchedule(true);
      setScheduleFormData({
        name: schedule.name,
        scheduleType: schedule.scheduleType,
        delayMinutes: schedule.delayMinutes || 60,
        scheduledDateTime: schedule.scheduledDateTime
          ? new Date(schedule.scheduledDateTime).toISOString().slice(0, 16)
          : "",
        dailyTime: schedule.dailyTime || "09:00",
        audienceType: schedule.audienceType,
        singleUser: schedule.singleUser?._id || "",
        manualEmailList: schedule.manualEmailList?.join(", ") || "",
        audienceFilters: {
          incompleteProfile: schedule.audienceFilters?.incompleteProfile || false,
          joinedBefore: schedule.audienceFilters?.joinedBefore
            ? new Date(schedule.audienceFilters.joinedBefore).toISOString().slice(0, 10)
            : "",
          joinedAfter: schedule.audienceFilters?.joinedAfter
            ? new Date(schedule.audienceFilters.joinedAfter).toISOString().slice(0, 10)
            : "",
          lastLoginBefore: schedule.audienceFilters?.lastLoginBefore
            ? new Date(schedule.audienceFilters.lastLoginBefore).toISOString().slice(0, 10)
            : "",
          lastLoginAfter: schedule.audienceFilters?.lastLoginAfter
            ? new Date(schedule.audienceFilters.lastLoginAfter).toISOString().slice(0, 10)
            : "",
        },
      });
    } else {
      setSelectedSchedule(null);
      setIsEditingSchedule(false);
      setScheduleFormData(scheduleInitialValues);
    }
    setAudiencePreview(null);
    setIsScheduleModalOpen(true);
  };

  const handleCloseScheduleModal = () => {
    setIsScheduleModalOpen(false);
    setSelectedSchedule(null);
    setIsEditingSchedule(false);
    setScheduleFormData(scheduleInitialValues);
    setAudiencePreview(null);
  };

  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        emailTemplate: selectedTemplate._id,
        name: scheduleFormData.name,
        scheduleType: scheduleFormData.scheduleType,
        audienceType: scheduleFormData.audienceType,
      };

      // Add schedule type specific fields
      if (scheduleFormData.scheduleType === "delay") {
        payload.delayMinutes = parseInt(scheduleFormData.delayMinutes);
      } else if (scheduleFormData.scheduleType === "datetime") {
        payload.scheduledDateTime = new Date(scheduleFormData.scheduledDateTime).toISOString();
      } else if (scheduleFormData.scheduleType === "daily") {
        payload.dailyTime = scheduleFormData.dailyTime;
      }

      // Add audience type specific fields
      if (scheduleFormData.audienceType === "single_user") {
        payload.singleUser = scheduleFormData.singleUser;
      } else if (scheduleFormData.audienceType === "manual_list") {
        payload.manualEmailList = scheduleFormData.manualEmailList
          .split(",")
          .map((email) => email.trim())
          .filter((email) => email);
      } else if (scheduleFormData.audienceType === "filtered_users") {
        payload.audienceFilters = {};
        if (scheduleFormData.audienceFilters.incompleteProfile) {
          payload.audienceFilters.incompleteProfile = true;
        }
        if (scheduleFormData.audienceFilters.joinedBefore) {
          payload.audienceFilters.joinedBefore = scheduleFormData.audienceFilters.joinedBefore;
        }
        if (scheduleFormData.audienceFilters.joinedAfter) {
          payload.audienceFilters.joinedAfter = scheduleFormData.audienceFilters.joinedAfter;
        }
        if (scheduleFormData.audienceFilters.lastLoginBefore) {
          payload.audienceFilters.lastLoginBefore = scheduleFormData.audienceFilters.lastLoginBefore;
        }
        if (scheduleFormData.audienceFilters.lastLoginAfter) {
          payload.audienceFilters.lastLoginAfter = scheduleFormData.audienceFilters.lastLoginAfter;
        }
      }

      if (isEditingSchedule && selectedSchedule) {
        await updateEmailScheduleAPI(selectedSchedule._id, payload);
        toast.success("Schedule updated successfully");
      } else {
        await createEmailScheduleAPI(payload);
        toast.success("Schedule created successfully");
      }

      fetchSchedulesForTemplate(selectedTemplate._id);
      handleCloseScheduleModal();
    } catch (error) {
      toast.error(error.response?.data?.message || "Error saving schedule");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (window.confirm("Are you sure you want to delete this schedule?")) {
      try {
        await deleteEmailScheduleAPI(scheduleId);
        toast.success("Schedule deleted successfully");
        fetchSchedulesForTemplate(selectedTemplate._id);
      } catch (error) {
        toast.error("Error deleting schedule");
      }
    }
  };

  const handlePauseSchedule = async (scheduleId) => {
    try {
      await pauseEmailScheduleAPI(scheduleId);
      toast.success("Schedule paused");
      fetchSchedulesForTemplate(selectedTemplate._id);
    } catch (error) {
      toast.error("Error pausing schedule");
    }
  };

  const handleResumeSchedule = async (scheduleId) => {
    try {
      await resumeEmailScheduleAPI(scheduleId);
      toast.success("Schedule resumed");
      fetchSchedulesForTemplate(selectedTemplate._id);
    } catch (error) {
      toast.error("Error resuming schedule");
    }
  };

  const handlePreviewAudience = async () => {
    try {
      const payload = {
        audienceType: scheduleFormData.audienceType,
      };

      if (scheduleFormData.audienceType === "single_user") {
        payload.singleUser = scheduleFormData.singleUser;
      } else if (scheduleFormData.audienceType === "manual_list") {
        payload.manualEmailList = scheduleFormData.manualEmailList
          .split(",")
          .map((email) => email.trim())
          .filter((email) => email);
      } else if (scheduleFormData.audienceType === "filtered_users") {
        payload.audienceFilters = scheduleFormData.audienceFilters;
      }

      const res = await previewAudienceAPI(payload);
      setAudiencePreview(res.data);
    } catch (error) {
      toast.error("Error previewing audience");
    }
  };

  // Get required variables for selected template type
  const getRequiredVariables = (templateType) => {
    const variablesMap = {
      welcome: ["#name#"],
      forgot_password: ["#otp#", "#code#"],
      admin_appointment: ["#name#", "#permissionsList#"],
      incomplete_profile: ["#name#", "#missingFields#", "#appLink#"],
      general: [],
    };
    return variablesMap[templateType] || [];
  };

  // Get template description
  const getTemplateDescription = (templateType) => {
    const descriptionsMap = {
      welcome: "Sent when a new user registers. Must include user's name.",
      forgot_password:
        "Sent when user requests password reset. Must include OTP/verification code.",
      admin_appointment:
        "Sent when admin is appointed or permissions are updated. Must include admin name and permissions list.",
      incomplete_profile:
        "Sent via cron job to users with incomplete profiles. Must include user's name, missing fields list, and app link.",
      general: "General purpose template for scheduled email campaigns.",
    };
    return descriptionsMap[templateType] || "";
  };

  const getScheduleTypeLabel = (type) => {
    const labels = {
      delay: "After Delay",
      datetime: "Specific Date/Time",
      daily: "Daily Recurring",
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: "bg-green-100 text-green-700",
      paused: "bg-yellow-100 text-yellow-700",
      completed: "bg-blue-100 text-blue-700",
      failed: "bg-red-100 text-red-700",
    };
    return badges[status] || "bg-gray-100 text-gray-700";
  };

  if (loader) {
    return (
      <div className="py-10 w-full flex justify-center items-center">
        <ButtonLoader />
      </div>
    );
  }

  return (
    <Suspense
      fallback={
        <div className="w-full pt-40 flex justify-center items-center">
          <ButtonLoader />
        </div>
      }
    >
      <Wrapper>
        {/* Header Section */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <FiMail className="w-8 h-8 text-[--primary]" />
              Email Templates
            </h1>
            <p className="text-gray-600 mt-2">
              Manage email templates sent to users
            </p>
          </div>
          <button
            onClick={handleCreate}
            className="bg-[--primary] text-white px-5 py-2.5 rounded-lg hover:bg-[--primary]/90 transition-colors font-medium flex items-center gap-2 shadow-sm"
          >
            <FiPlus className="w-5 h-5" />
            Create Template
          </button>
        </div>

        {/* Info Banner */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
          <FiAlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-gray-700 font-medium">
              Available Variables
            </p>
            <div className="text-sm text-gray-600 mt-2 space-y-2">
              <div>
                <span className="font-medium">Welcome Email:</span> Must include{" "}
                <code className="bg-white px-2 py-0.5 rounded">#name#</code>
              </div>
              <div>
                <span className="font-medium">Forgot Password:</span> Must
                include{" "}
                <code className="bg-white px-2 py-0.5 rounded">#otp#</code> or{" "}
                <code className="bg-white px-2 py-0.5 rounded">#code#</code>
              </div>
              <div>
                <span className="font-medium">Admin Appointment:</span> Must
                include{" "}
                <code className="bg-white px-2 py-0.5 rounded">#name#</code> and{" "}
                <code className="bg-white px-2 py-0.5 rounded">
                  #permissionsList#
                </code>
              </div>
              <div>
                <span className="font-medium">Incomplete Profile:</span> Must
                include{" "}
                <code className="bg-white px-2 py-0.5 rounded">#name#</code>,{" "}
                <code className="bg-white px-2 py-0.5 rounded">
                  #missingFields#
                </code>
                , and{" "}
                <code className="bg-white px-2 py-0.5 rounded">#appLink#</code>
              </div>
              <div>
                <span className="font-medium">General:</span> Supports{" "}
                <code className="bg-white px-2 py-0.5 rounded">#name#</code>,{" "}
                <code className="bg-white px-2 py-0.5 rounded">#email#</code>,{" "}
                <code className="bg-white px-2 py-0.5 rounded">#firstName#</code>,{" "}
                <code className="bg-white px-2 py-0.5 rounded">#lastName#</code>{" "}
                - Can be scheduled!
              </div>
              <p className="mt-2 text-xs text-gray-500">
                These variables will be replaced with actual values when
                emails are sent.
              </p>
            </div>
          </div>
        </div>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 gap-6">
          {templates.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
              <FiMail className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No Templates Found
              </h3>
              <p className="text-gray-600 mb-6">
                Create your first email template to get started
              </p>
              <button
                onClick={handleCreate}
                className="bg-[--primary] text-white px-5 py-2.5 rounded-lg hover:bg-[--primary]/90 transition-colors font-medium inline-flex items-center gap-2"
              >
                <FiPlus className="w-5 h-5" />
                Create Template
              </button>
            </div>
          ) : (
            templates.map((template) => (
              <div
                key={template._id}
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
              >
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold text-gray-900 capitalize">
                        {template.templateType.replace("_", " ")}
                      </h3>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          template.isActive
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {template.isActive ? "Active" : "Inactive"}
                      </span>
                      {template.templateType === "general" && (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 flex items-center gap-1">
                          <FiClock className="w-3 h-3" />
                          Schedulable
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {template.subject}
                    </p>
                    {template.lastModifiedBy && (
                      <p className="text-xs text-gray-500 mt-1">
                        Last modified by: {template.lastModifiedBy.first_name}{" "}
                        {template.lastModifiedBy.last_name} on{" "}
                        {new Date(template.updatedAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {template.templateType === "general" && (
                      <button
                        onClick={() =>
                          navigate(`/email-logs?templateId=${template._id}`)
                        }
                        className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                        title="View logs"
                      >
                        <FiList className="w-5 h-5" />
                      </button>
                    )}
                    <button
                      onClick={() => handlePreview(template.htmlContent)}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Preview"
                    >
                      <FiEye className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleToggleStatus(template._id)}
                      className="p-2 text-gray-600 hover:text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                      title={template.isActive ? "Deactivate" : "Activate"}
                    >
                      {template.isActive ? (
                        <FiEyeOff className="w-5 h-5" />
                      ) : (
                        <FiEye className="w-5 h-5" />
                      )}
                    </button>
                    <button
                      onClick={() => handleEdit(template)}
                      className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <FiEdit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(template._id)}
                      className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <FiTrash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex gap-2 flex-wrap">
                    {template.variables?.map((variable, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-mono"
                      >
                        {variable}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Create/Edit Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
                <h2 className="text-xl font-semibold text-gray-900">
                  {isEditing ? "Edit Template" : "Create Template"}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              <div className="flex flex-col lg:flex-row">
                {/* Main Form */}
                <form onSubmit={formik.handleSubmit} className="p-6 flex-1">
                  <div className="space-y-6">
                    {/* Template Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Template Type
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <select
                        name="templateType"
                        value={formik.values.templateType}
                        onChange={formik.handleChange}
                        disabled={isEditing}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-transparent transition-colors disabled:bg-gray-100"
                      >
                        <option value="welcome">Welcome Email</option>
                        <option value="forgot_password">Forgot Password</option>
                        <option value="admin_appointment">
                          Admin Appointment
                        </option>
                        <option value="incomplete_profile">
                          Incomplete Profile Reminder
                        </option>
                        <option value="general">General (Schedulable)</option>
                      </select>
                      {formik.touched.templateType &&
                        formik.errors.templateType && (
                          <p className="text-red-500 text-sm mt-1">
                            {formik.errors.templateType}
                          </p>
                        )}

                      {/* Template Description & Required Variables */}
                      {formik.values.templateType && (
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm text-gray-700 mb-2">
                            <span className="font-medium">Description:</span>{" "}
                            {getTemplateDescription(formik.values.templateType)}
                          </p>
                          {getRequiredVariables(formik.values.templateType)
                            .length > 0 && (
                            <p className="text-sm text-gray-700">
                              <span className="font-medium">
                                Required Variables:
                              </span>{" "}
                              {getRequiredVariables(
                                formik.values.templateType
                              ).map((variable, idx) => (
                                <code
                                  key={idx}
                                  className="bg-white px-2 py-0.5 rounded mx-1"
                                >
                                  {variable}
                                </code>
                              ))}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Subject */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Subject
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <input
                        name="subject"
                        type="text"
                        placeholder="Welcome to Tykoon!"
                        value={formik.values.subject}
                        onChange={formik.handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-transparent transition-colors"
                      />
                      {formik.touched.subject && formik.errors.subject && (
                        <p className="text-red-500 text-sm mt-1">
                          {formik.errors.subject}
                        </p>
                      )}
                    </div>

                    {/* HTML Content */}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Email Content
                          <span className="text-red-500 ml-1">*</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => setShowHtmlEditor(!showHtmlEditor)}
                          className="text-sm text-gray-600 hover:text-[--primary] flex items-center gap-1"
                        >
                          <FiCode className="w-4 h-4" />
                          {showHtmlEditor ? "Visual Editor" : "HTML Code"}
                        </button>
                      </div>

                      {showHtmlEditor ? (
                        // HTML Code Editor
                        <textarea
                          name="htmlContent"
                          placeholder="Enter HTML content here..."
                          value={formik.values.htmlContent}
                          onChange={formik.handleChange}
                          rows={15}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-transparent transition-colors font-mono text-sm"
                        />
                      ) : (
                        // Rich Text Editor
                        <div className="border border-gray-300 rounded-lg overflow-hidden">
                          <ReactQuill
                            theme="snow"
                            value={formik.values.htmlContent}
                            onChange={(content) =>
                              formik.setFieldValue("htmlContent", content)
                            }
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Write your email content here..."
                            className="bg-white"
                            style={{ minHeight: "300px" }}
                          />
                        </div>
                      )}

                      {formik.touched.htmlContent &&
                        formik.errors.htmlContent && (
                          <p className="text-red-500 text-sm mt-1">
                            {formik.errors.htmlContent}
                          </p>
                        )}

                      <div className="flex gap-3 mt-2">
                        <button
                          type="button"
                          onClick={() => handlePreview(formik.values.htmlContent)}
                          className="text-sm text-[--primary] hover:underline flex items-center gap-1"
                        >
                          <FiEye className="w-4 h-4" />
                          Preview Template
                        </button>
                      </div>

                      <p className="text-xs text-gray-500 mt-2">
                        Use variables like{" "}
                        <code className="bg-gray-100 px-1 rounded">#name#</code>{" "}
                        in your content. They will be replaced with actual values.
                      </p>
                    </div>

                    {/* Variables */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Variables (comma-separated)
                      </label>
                      <input
                        name="variables"
                        type="text"
                        placeholder="#name#, #email#, #otp#"
                        value={formik.values.variables}
                        onChange={formik.handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-transparent transition-colors"
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        List all variables used in your template.
                      </p>
                    </div>

                    {/* Is Active */}
                    <div className="flex items-center gap-3">
                      <input
                        name="isActive"
                        type="checkbox"
                        checked={formik.values.isActive}
                        onChange={formik.handleChange}
                        className="w-5 h-5 text-[--primary] border-gray-300 rounded focus:ring-[--primary]"
                      />
                      <label className="text-sm font-medium text-gray-700">
                        Active Template
                      </label>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-[--primary] text-white px-6 py-3 rounded-lg hover:bg-[--primary]/90 transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <FiSave className="w-4 h-4" />
                          {isEditing ? "Update" : "Create"} Template
                        </>
                      )}
                    </button>
                  </div>
                </form>

                {/* Scheduling Panel - Only for general templates in edit mode */}
                {isEditing && selectedTemplate?.templateType === "general" && (
                  <div className="lg:w-96 border-t lg:border-t-0 lg:border-l border-gray-200 p-6 bg-gray-50">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <FiClock className="w-5 h-5 text-purple-600" />
                        Schedules
                      </h3>
                      <button
                        type="button"
                        onClick={() => handleOpenScheduleModal()}
                        className="bg-purple-600 text-white px-3 py-1.5 rounded-lg hover:bg-purple-700 transition-colors text-sm flex items-center gap-1"
                      >
                        <FiPlus className="w-4 h-4" />
                        Add
                      </button>
                    </div>

                    {scheduleLoader ? (
                      <div className="flex justify-center py-8">
                        <ButtonLoader />
                      </div>
                    ) : schedules.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <FiCalendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-sm">No schedules yet</p>
                        <p className="text-xs mt-1">
                          Create a schedule to send this template automatically
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-[400px] overflow-y-auto">
                        {schedules.map((schedule) => (
                          <div
                            key={schedule._id}
                            className="bg-white rounded-lg border border-gray-200 p-3"
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <p className="font-medium text-gray-900 text-sm">
                                  {schedule.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {getScheduleTypeLabel(schedule.scheduleType)}
                                </p>
                              </div>
                              <span
                                className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(
                                  schedule.status
                                )}`}
                              >
                                {schedule.status}
                              </span>
                            </div>

                            <div className="text-xs text-gray-600 mb-2">
                              <div className="flex items-center gap-1">
                                <FiUsers className="w-3 h-3" />
                                {schedule.audienceType === "single_user" && "Single User"}
                                {schedule.audienceType === "manual_list" && 
                                  `${schedule.manualEmailList?.length || 0} emails`}
                                {schedule.audienceType === "filtered_users" && "Filtered Users"}
                              </div>
                              {schedule.nextRunAt && (
                                <div className="flex items-center gap-1 mt-1">
                                  <FiClock className="w-3 h-3" />
                                  Next: {new Date(schedule.nextRunAt).toLocaleString()}
                                </div>
                              )}
                            </div>

                            <div className="flex gap-1 flex-wrap">
                              <button
                                onClick={() =>
                                  navigate(
                                    `/email-logs?scheduleId=${schedule._id}`
                                  )
                                }
                                className="p-1.5 text-purple-600 hover:bg-purple-50 rounded transition-colors"
                                title="View logs"
                              >
                                <FiList className="w-4 h-4" />
                              </button>
                              {schedule.status === "active" ? (
                                <button
                                  onClick={() => handlePauseSchedule(schedule._id)}
                                  className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded transition-colors"
                                  title="Pause"
                                >
                                  <FiPause className="w-4 h-4" />
                                </button>
                              ) : schedule.status === "paused" ? (
                                <button
                                  onClick={() => handleResumeSchedule(schedule._id)}
                                  className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                                  title="Resume"
                                >
                                  <FiPlay className="w-4 h-4" />
                                </button>
                              ) : null}
                              <button
                                onClick={() => handleOpenScheduleModal(schedule)}
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                title="Edit"
                              >
                                <FiEdit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteSchedule(schedule._id)}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Delete"
                              >
                                <FiTrash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Recent logs for this template */}
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                          <FiList className="w-4 h-4 text-purple-600" />
                          Recent logs
                        </h4>
                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              `/email-logs?templateId=${selectedTemplate._id}`
                            )
                          }
                          className="text-xs text-purple-600 hover:underline flex items-center gap-1"
                        >
                          View all
                          <FiExternalLink className="w-3 h-3" />
                        </button>
                      </div>
                      {logsLoader ? (
                        <div className="flex justify-center py-4">
                          <ButtonLoader />
                        </div>
                      ) : templateLogs.length === 0 ? (
                        <p className="text-xs text-gray-500 py-2">
                          No logs yet. Logs appear when scheduled campaigns send emails.
                        </p>
                      ) : (
                        <div className="space-y-2 max-h-[200px] overflow-y-auto">
                          {templateLogs.map((log) => (
                            <div
                              key={log._id}
                              className="bg-white rounded border border-gray-200 p-2 text-xs"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span
                                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${
                                    log.status === "sent"
                                      ? "bg-green-100 text-green-700"
                                      : "bg-red-100 text-red-700"
                                  }`}
                                >
                                  {log.status === "sent" ? (
                                    <FiCheckCircle className="w-3 h-3" />
                                  ) : (
                                    <FiXCircle className="w-3 h-3" />
                                  )}
                                  {log.status}
                                </span>
                                <span className="text-gray-500 truncate max-w-[120px]">
                                  {new Date(log.sentAt).toLocaleString()}
                                </span>
                              </div>
                              <p className="text-gray-700 truncate mt-1 font-medium">
                                {log.recipientEmail}
                              </p>
                              {log.status === "failed" && log.errorMessage && (
                                <p className="text-red-600 truncate mt-0.5">
                                  {log.errorMessage}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Schedule Modal */}
        {isScheduleModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
                <h2 className="text-xl font-semibold text-gray-900">
                  {isEditingSchedule ? "Edit Schedule" : "Create Schedule"}
                </h2>
                <button
                  onClick={handleCloseScheduleModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleScheduleSubmit} className="p-6 space-y-6">
                {/* Schedule Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Schedule Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={scheduleFormData.name}
                    onChange={(e) =>
                      setScheduleFormData({ ...scheduleFormData, name: e.target.value })
                    }
                    placeholder="e.g., Weekly Newsletter"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Schedule Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Schedule Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={scheduleFormData.scheduleType}
                    onChange={(e) =>
                      setScheduleFormData({ ...scheduleFormData, scheduleType: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="delay">After X Minutes (One-time)</option>
                    <option value="datetime">Specific Date & Time (One-time)</option>
                    <option value="daily">Daily at Time (Recurring)</option>
                  </select>
                </div>

                {/* Schedule Type Specific Fields */}
                {scheduleFormData.scheduleType === "delay" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Delay (minutes) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={scheduleFormData.delayMinutes}
                      onChange={(e) =>
                        setScheduleFormData({
                          ...scheduleFormData,
                          delayMinutes: parseInt(e.target.value),
                        })
                      }
                      min="1"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Email will be sent {scheduleFormData.delayMinutes} minutes after schedule is activated
                    </p>
                  </div>
                )}

                {scheduleFormData.scheduleType === "datetime" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date & Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={scheduleFormData.scheduledDateTime}
                      onChange={(e) =>
                        setScheduleFormData({
                          ...scheduleFormData,
                          scheduledDateTime: e.target.value,
                        })
                      }
                      min={new Date().toISOString().slice(0, 16)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      required
                    />
                  </div>
                )}

                {scheduleFormData.scheduleType === "daily" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Daily Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="time"
                      value={scheduleFormData.dailyTime}
                      onChange={(e) =>
                        setScheduleFormData({
                          ...scheduleFormData,
                          dailyTime: e.target.value,
                        })
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Email will be sent every day at this time (server time)
                    </p>
                  </div>
                )}

                {/* Audience Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Audience <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={scheduleFormData.audienceType}
                    onChange={(e) =>
                      setScheduleFormData({ ...scheduleFormData, audienceType: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="manual_list">Manual Email List</option>
                    <option value="single_user">Single User</option>
                    <option value="filtered_users">Filtered Users</option>
                  </select>
                </div>

                {/* Audience Type Specific Fields */}
                {scheduleFormData.audienceType === "single_user" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Search User <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        placeholder="Search by name or email..."
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    {userSearchLoading && (
                      <p className="text-sm text-gray-500 mt-2">Searching...</p>
                    )}
                    {users.length > 0 && (
                      <div className="mt-2 border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
                        {users.map((user) => (
                          <button
                            key={user._id}
                            type="button"
                            onClick={() => {
                              setScheduleFormData({
                                ...scheduleFormData,
                                singleUser: user._id,
                              });
                              setUserSearch(`${user.firstName || ""} ${user.lastName || ""} (${user.email})`);
                              setUsers([]);
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-gray-50 border-b last:border-b-0"
                          >
                            <p className="font-medium text-sm">
                              {user.firstName || ""} {user.lastName || ""}
                            </p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {scheduleFormData.audienceType === "manual_list" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Addresses <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={scheduleFormData.manualEmailList}
                      onChange={(e) =>
                        setScheduleFormData({
                          ...scheduleFormData,
                          manualEmailList: e.target.value,
                        })
                      }
                      placeholder="email1@example.com, email2@example.com"
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Enter email addresses separated by commas
                    </p>
                  </div>
                )}

                {scheduleFormData.audienceType === "filtered_users" && (
                  <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-700">Filter Criteria</p>

                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="incompleteProfile"
                        checked={scheduleFormData.audienceFilters.incompleteProfile}
                        onChange={(e) =>
                          setScheduleFormData({
                            ...scheduleFormData,
                            audienceFilters: {
                              ...scheduleFormData.audienceFilters,
                              incompleteProfile: e.target.checked,
                            },
                          })
                        }
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <label htmlFor="incompleteProfile" className="text-sm text-gray-700">
                        Users with incomplete profiles
                      </label>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Joined After</label>
                        <input
                          type="date"
                          value={scheduleFormData.audienceFilters.joinedAfter}
                          onChange={(e) =>
                            setScheduleFormData({
                              ...scheduleFormData,
                              audienceFilters: {
                                ...scheduleFormData.audienceFilters,
                                joinedAfter: e.target.value,
                              },
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Joined Before</label>
                        <input
                          type="date"
                          value={scheduleFormData.audienceFilters.joinedBefore}
                          onChange={(e) =>
                            setScheduleFormData({
                              ...scheduleFormData,
                              audienceFilters: {
                                ...scheduleFormData.audienceFilters,
                                joinedBefore: e.target.value,
                              },
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Last Active After</label>
                        <input
                          type="date"
                          value={scheduleFormData.audienceFilters.lastLoginAfter}
                          onChange={(e) =>
                            setScheduleFormData({
                              ...scheduleFormData,
                              audienceFilters: {
                                ...scheduleFormData.audienceFilters,
                                lastLoginAfter: e.target.value,
                              },
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Last Active Before</label>
                        <input
                          type="date"
                          value={scheduleFormData.audienceFilters.lastLoginBefore}
                          onChange={(e) =>
                            setScheduleFormData({
                              ...scheduleFormData,
                              audienceFilters: {
                                ...scheduleFormData.audienceFilters,
                                lastLoginBefore: e.target.value,
                              },
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Audience Preview */}
                <div>
                  <button
                    type="button"
                    onClick={handlePreviewAudience}
                    className="text-sm text-purple-600 hover:underline flex items-center gap-1"
                  >
                    <FiUsers className="w-4 h-4" />
                    Preview Audience
                  </button>

                  {audiencePreview && (
                    <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                      <p className="text-sm font-medium text-purple-900">
                        Total Recipients: {audiencePreview.totalCount}
                      </p>
                      {audiencePreview.sample?.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-purple-700 mb-1">Sample:</p>
                          <div className="flex flex-wrap gap-1">
                            {audiencePreview.sample.slice(0, 5).map((item, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-0.5 bg-white rounded text-xs text-gray-700"
                              >
                                {item.email}
                              </span>
                            ))}
                            {audiencePreview.totalCount > 5 && (
                              <span className="px-2 py-0.5 text-xs text-purple-600">
                                +{audiencePreview.totalCount - 5} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCloseScheduleModal}
                    className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <FiSave className="w-4 h-4" />
                        {isEditingSchedule ? "Update" : "Create"} Schedule
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Preview Modal */}
        {showPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
                <h2 className="text-xl font-semibold text-gray-900">
                  Email Preview
                </h2>
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6">
                <div
                  className="border border-gray-200 rounded-lg p-4"
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              </div>
            </div>
          </div>
        )}
      </Wrapper>
    </Suspense>
  );
};

export default EmailTemplates;
